'use client';

import { useCallback } from 'react';

import { apiFetch } from '@libs/core';

import { SnackbarUtils } from '../utils/snackbarUtils';

// Error type definitions for cleaner type checking
interface HttpError {
    status?: number;
    response?: {
        status?: number;
    };
}

interface NetworkError {
    name?: string;
    message?: string;
}

// Type guard functions
const getErrorStatus = (error: unknown): number | undefined => {
    const httpError = error as HttpError;
    return httpError?.status ?? httpError?.response?.status;
};

const isNetworkError = (error: unknown): boolean => {
    const networkError = error as NetworkError;
    const { name, message } = networkError;
    return !getErrorStatus(error) && (name === 'TypeError' || (message?.includes('fetch') ?? false));
};

const isServerError = (status?: number): boolean => {
    return status !== undefined && status >= 500 && status < 600;
};

const showApiErrorSnackbar = () => SnackbarUtils.showPredefined('SERVER_ERROR');

/**
 * Custom hook that provides a fetch wrapper with automatic 500 error handling via snackbar
 *
 * Features:
 * - Automatically detects 5xx server errors
 * - Shows user-friendly snackbar with refresh option
 * - Filters out 4xx client errors (validation, auth, etc.)
 * - Maintains original error handling for non-server errors
 */
export const useFetchWithSnackbarErrorHandling = () => {
    /**
     * Fetch wrapper that handles 500+ errors with snackbar notifications
     *
     * @param url - The URL to fetch
     * @param options - Standard RequestInit options plus additionalSuccessCodes
     * @returns Promise<T | null> - Response data or null if error occurred
     *
     * Behavior:
     * - 500-599 errors: Shows snackbar, returns null
     * - 400-499 errors: Normal handling via typedFetch (for form validation, etc.)
     * - 200-299 success: Normal response
     * - Network errors: Shows snackbar, returns null
     */
    const fetchWithSnackbarErrorHandling = useCallback(
        async <TResponse = Record<string, unknown>>(
            url: string,
            options?: RequestInit & { additionalSuccessCodes?: number[] }
        ): Promise<TResponse | null> => {
            try {
                const result = await apiFetch<TResponse>(url, options);

                // apiFetch returns Response object for errors, Error object for network issues, or actual data for success
                if (result instanceof Response) {
                    // Handle 5xx server errors
                    if (result.status >= 500 && result.status < 600) {
                        SnackbarUtils.showPredefined('SERVER_ERROR');
                        return null;
                    }
                    // For 4xx errors, we need to throw to preserve existing validation handling
                    throw result;
                } else if (result instanceof Error) {
                    // Handle network errors
                    if (isNetworkError(result)) {
                        SnackbarUtils.showPredefined('SERVER_ERROR');
                        return null;
                    }
                    // Re-throw other errors
                    throw result;
                }

                // Return successful data
                return result;
            } catch (error: unknown) {
                // Handle any errors thrown by apiFetch or re-thrown above
                if (error instanceof Response) {
                    // This is a 4xx error that should be handled normally
                    throw error;
                }

                const status = getErrorStatus(error);

                // Handle 5xx server errors (fallback)
                if (isServerError(status)) {
                    SnackbarUtils.showPredefined('SERVER_ERROR');
                    return null;
                }

                // Handle network errors (fallback)
                if (isNetworkError(error)) {
                    SnackbarUtils.showPredefined('SERVER_ERROR');
                    return null;
                }

                // Re-throw other errors (4xx, etc.) to preserve existing handling
                throw error;
            }
        },
        []
    );

    // Simple helper to extract status from error (avoids code duplication)
    const getErrorStatus = (error: unknown): number | undefined => {
        const err = error as { status?: number; response?: { status?: number } };
        return err?.status ?? err?.response?.status;
    };

    return {
        fetchWithSnackbarErrorHandling,
        showApiErrorSnackbar,
        getErrorStatus,
        isServerError,
    };
};

export type UseFetchWithSnackbarErrorHandling = ReturnType<typeof useFetchWithSnackbarErrorHandling>;
